import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import UbicacionSearch from '../../components/UbicacionSearch';
import { useAutoUbicacionPredeterminada } from '../../hooks/useAutoUbicacionPredeterminada';
import { IUbicacion } from '../../interfaces/Ubicacion';
import api from '../../services/api';
import comprasAnalisisService, { PreviewGrupo, PreviewGrupoLinea, PreviewLineaInput } from '../../services/comprasAnalisisService';
import ordenCompraService from '../../services/ordenCompraService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatCurrency } from '../../utils/formatters';

type LocationState = {
  lineas: PreviewLineaInput[];
};

interface SucursalVentaOption {
  nroSuc: number;
  nombreSucursal?: string;
}

const STORAGE_NRO_SUC_OC = 'nroSuc';

const FORMAS_DE_PAGO = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'DEVOLUCION',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA',
];

const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

const resolveCostoBrutoUnitario = (linea: PreviewGrupoLinea): number | null => {
  const bruto = Number(linea.costoBrutoUnitario);
  if (Number.isFinite(bruto) && bruto > 0) return bruto;
  const neto = Number(linea.costoUnitario);
  if (Number.isFinite(neto) && neto > 0) return neto;
  return null;
};

const parseDecimalInput = (raw: string): number | null => {
  const normalized = String(raw ?? '')
    .replace(/[^0-9.,]/g, '')
    .replace(/,/g, '.');
  if (!normalized || normalized === '.') return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const CurrencyEditField: React.FC<{
  value: number;
  onChange: (value: number) => void;
  width?: number;
}> = ({ value, onChange, width = 140 }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  const handleFocus = () => {
    setDraft(String(value ?? 0));
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseDecimalInput(draft);
    const next = parsed == null ? 0 : Math.max(0, parsed);
    onChange(next);
    setDraft('');
  };

  return (
    <TextField
      size="small"
      value={focused ? draft : formatCurrency(value ?? 0)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e) => {
        let raw = String(e.target.value ?? '');
        raw = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
        const parts = raw.split('.');
        if (parts.length > 2) raw = `${parts[0]}.${parts.slice(1).join('')}`;
        setDraft(raw);
        const parsed = parseDecimalInput(raw);
        if (parsed != null) onChange(Math.max(0, parsed));
      }}
      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
      sx={{ width, '& input': { textAlign: 'right' } }}
    />
  );
};

const SugerenciaCompraPreview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const state = location.state as LocationState | undefined;

  const [grupos, setGrupos] = useState<PreviewGrupo[]>([]);
  const [sinProveedor, setSinProveedor] = useState<PreviewLineaInput[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nroSuc, setNroSuc] = useState<number>(0);
  const [nroUbicacionDestino, setNroUbicacionDestino] = useState<number | undefined>(undefined);
  const [ubicacionNombre, setUbicacionNombre] = useState('');
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);
  const [sucursales, setSucursales] = useState<SucursalVentaOption[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  const lineasKey = useMemo(() => JSON.stringify(state?.lineas ?? []), [state?.lineas]);

  const sucursalesOrdenadas = useMemo(
    () => [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc),
    [sucursales],
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingSucursales(true);
        const all: SucursalVentaOption[] = [];
        let page = 1;
        const pageSize = 20;
        while (true) {
          const response = await api.get('/api/SucursalesVenta', {
            params: { PageNumber: page, PageSize: pageSize },
          });
          const data = response.data;
          const batch: SucursalVentaOption[] = Array.isArray(data?.items) ? data.items : [];
          all.push(...batch);
          const totalPages = typeof data?.totalPages === 'number' ? data.totalPages : 1;
          if (page >= totalPages || batch.length === 0) break;
          page += 1;
        }
        setSucursales(all);
      } catch {
        setSucursales([]);
      } finally {
        setLoadingSucursales(false);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (sucursales.length === 0 || nroSuc > 0) return;
    const stored = parseInt(localStorage.getItem(STORAGE_NRO_SUC_OC) || '', 10);
    const fromStore = Number.isFinite(stored) && stored > 0 ? stored : null;
    const match = fromStore ? sucursales.find((s) => s.nroSuc === fromStore) : undefined;
    const pick = match?.nroSuc ?? sucursalesOrdenadas[0]?.nroSuc;
    if (pick) setNroSuc(pick);
  }, [sucursales, nroSuc, sucursalesOrdenadas]);

  const applyUbicacionPredeterminada = useCallback((nro: number, nombre: string) => {
    setNroUbicacionDestino(nro);
    setUbicacionNombre(nombre);
  }, []);

  useAutoUbicacionPredeterminada({
    nroUbicacionDestino,
    onApply: applyUbicacionPredeterminada,
  });

  useEffect(() => {
    if (!state?.lineas?.length) {
      enqueueSnackbar('No hay líneas para previsualizar.', { variant: 'warning' });
      navigate('/compras/informes/sugerencia-compra');
      return;
    }
    const run = async () => {
      try {
        setLoading(true);
        const preview = await comprasAnalisisService.buildPreview({
          lineas: state.lineas,
        });
        setGrupos(preview.grupos || []);
        setSinProveedor(preview.sinProveedor || []);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'No se pudo construir el preview.'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [lineasKey, navigate, enqueueSnackbar, state?.lineas]);

  const totalGeneral = useMemo(
    () => grupos.reduce((sum, g) => sum + g.lineas.reduce((s, l) => s + (l.cantidad * l.costoUnitario), 0), 0),
    [grupos],
  );

  const updateGrupo = (index: number, updater: (g: PreviewGrupo) => PreviewGrupo) => {
    setGrupos((prev) => prev.map((g, i) => (i === index ? updater(g) : g)));
  };

  const handleRemoveLinea = (grupoIndex: number, lineaIndex: number) => {
    setGrupos((prev) =>
      prev
        .map((g, i) => {
          if (i !== grupoIndex) return g;
          return { ...g, lineas: g.lineas.filter((_, li) => li !== lineaIndex) };
        })
        .filter((g) => g.lineas.length > 0),
    );
  };

  const handleUbicacionSelect = (ubicacion: IUbicacion) => {
    setNroUbicacionDestino(ubicacion.nroUbicacion);
    setUbicacionNombre(ubicacion.nombreUbicacion || `Ubicación ${ubicacion.nroUbicacion}`);
    setOpenUbicacionSearch(false);
  };

  const handleSucursalChange = (value: number | null) => {
    const next = value ?? 0;
    setNroSuc(next);
    if (next > 0) localStorage.setItem(STORAGE_NRO_SUC_OC, String(next));
  };

  const tieneLineasParaCrear = useMemo(
    () => grupos.some((g) => g.lineas.some((l) => l.cantidad > 0)),
    [grupos],
  );

  const handleConfirmar = async () => {
    if (!nroSuc || nroSuc <= 0) {
      enqueueSnackbar('Seleccione una sucursal.', { variant: 'warning' });
      return;
    }
    if (!nroUbicacionDestino || nroUbicacionDestino <= 0) {
      enqueueSnackbar('Seleccione ubicación destino.', { variant: 'warning' });
      return;
    }
    if (!tieneLineasParaCrear) {
      enqueueSnackbar('No hay líneas para crear órdenes.', { variant: 'warning' });
      return;
    }

    const lineasSinCosto = grupos.flatMap((g) =>
      g.lineas
        .filter((l) => l.cantidad > 0 && resolveCostoBrutoUnitario(l) == null)
        .map((l) => l.codigoArticulo),
    );
    if (lineasSinCosto.length > 0) {
      const muestra = lineasSinCosto.slice(0, 5).join(', ');
      const sufijo = lineasSinCosto.length > 5 ? '…' : '';
      enqueueSnackbar(
        `Hay ${lineasSinCosto.length} línea(s) sin costo unitario válido (> 0): ${muestra}${sufijo}. Revise costos por proveedor o elimine esas líneas.`,
        { variant: 'error', autoHideDuration: 12000 },
      );
      return;
    }

    try {
      setSaving(true);
      const creadas: number[] = [];
      const errores: string[] = [];

      for (const grupo of grupos) {
        if (!grupo.nroProveedor || grupo.nroProveedor <= 0) {
          errores.push('Hay un grupo sin proveedor válido.');
          continue;
        }

        const items = grupo.lineas
          .filter((l) => l.cantidad > 0)
          .map((l) => {
            const costoBrutoUnitario = resolveCostoBrutoUnitario(l);
            if (costoBrutoUnitario == null) return null;
            return {
              codigoArticulo: l.codigoArticulo.trim(),
              descripcion: l.descripcion?.trim() || undefined,
              cantidad: Number(l.cantidad),
              costoBrutoUnitario,
              porcDesc1: Number(l.porcDesc1) || 0,
              porcDesc2: Number(l.porcDesc2) || 0,
              porcDesc3: Number(l.porcDesc3) || 0,
              porcDesc4: Number(l.porcDesc4) || 0,
              porcDesc5: Number(l.porcDesc5) || 0,
              um: (l.umCompra || '').trim() || undefined,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item != null);

        if (items.length === 0) {
          continue;
        }

        const formaDePago = (grupo.formaDePago || '').trim() || 'CONTADO';

        const payload = {
          formulario: {
            nroSuc: Number(grupo.nroSuc || nroSuc),
            nroProveedor: Number(grupo.nroProveedor),
            descripcion: 'Generada desde sugerencia de compra',
            fechaEmision: todayIsoDate(),
            formaDePago,
            nroUbicacionDestino: Number(grupo.nroUbicacionDestino || nroUbicacionDestino),
            porcDesc: 0,
            porcDesc2: 0,
            porcDesc3: 0,
          },
          items,
        };

        try {
          const creada = await ordenCompraService.createOrdenCompra(payload);
          creadas.push(creada.nroFormulario);
        } catch (error) {
          errores.push(`Proveedor ${grupo.nroProveedor}: ${getApiErrorMessage(error, 'Error al crear OC')}`);
        }
      }

      if (creadas.length > 0) {
        enqueueSnackbar(`Se crearon ${creadas.length} órdenes: ${creadas.join(', ')}`, { variant: 'success', autoHideDuration: 8000 });
      }
      if (errores.length > 0) {
        const detalle = errores.slice(0, 3).join(' · ');
        const extra = errores.length > 3 ? ` (+${errores.length - 3} más)` : '';
        enqueueSnackbar(
          errores.length === 1 ? errores[0] : `Errores al crear órdenes: ${detalle}${extra}`,
          { variant: 'error', autoHideDuration: 12000 },
        );
      }
      if (creadas.length > 0) {
        navigate('/compras/ordenes');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/compras/informes/sugerencia-compra')}>
                Volver al análisis
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Vista previa de órdenes por proveedor
              </Typography>
            </Stack>
            <Button
              variant="contained"
              disabled={saving || loading || !tieneLineasParaCrear}
              onClick={() => void handleConfirmar()}
            >
              {saving ? 'Creando órdenes...' : 'Confirmar y crear órdenes'}
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect<number>
                label="Nº Sucursal"
                required
                disabled={loadingSucursales || sucursalesOrdenadas.length === 0}
                loading={loadingSucursales}
                allowEmpty
                emptyOptionLabel={
                  loadingSucursales
                    ? 'Cargando…'
                    : sucursalesOrdenadas.length === 0
                      ? 'Sin sucursales'
                      : 'Seleccione'
                }
                value={nroSuc > 0 ? nroSuc : null}
                onChange={handleSucursalChange}
                options={sucursalesOrdenadas.map((s) => ({
                  value: s.nroSuc,
                  label: `${s.nroSuc} - ${s.nombreSucursal?.trim() || `Sucursal ${s.nroSuc}`}`,
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Ubicación destino"
                value={ubicacionNombre || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOpenUbicacionSearch(true)} edge="end" size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onClick={() => setOpenUbicacionSearch(true)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 1.5 }}>
                <Typography variant="body2">Total estimado</Typography>
                <Typography variant="h6">{formatCurrency(totalGeneral)}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {sinProveedor.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff8e1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Ítems sin proveedor (no se crearán)
              </Typography>
              {sinProveedor.map((x) => (
                <Typography key={`${x.codigoArticulo}-${x.cantidad}`} variant="body2">
                  {x.codigoArticulo} - Cantidad {x.cantidad}
                </Typography>
              ))}
            </Paper>
          )}

          {grupos.map((grupo, grupoIndex) => (
            <Card key={`${grupo.nroProveedor}-${grupoIndex}`} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      label="Proveedor"
                      value={grupo.nroProveedor}
                      onChange={(e) => updateGrupo(grupoIndex, (g) => ({ ...g, nroProveedor: Number(e.target.value) || g.nroProveedor }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SearchableSelect
                      label="Forma de pago"
                      value={grupo.formaDePago || 'CONTADO'}
                      onChange={(v) => updateGrupo(grupoIndex, (g) => ({ ...g, formaDePago: v ?? 'CONTADO' }))}
                      options={FORMAS_DE_PAGO.map((fp) => ({ value: fp, label: fp }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{grupo.nombreProveedor || `Proveedor ${grupo.nroProveedor}`}</strong> - Total:{' '}
                      {formatCurrency(grupo.lineas.reduce((s, l) => s + l.cantidad * l.costoUnitario, 0))}
                    </Typography>
                  </Grid>
                </Grid>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell>UM</TableCell>
                        <TableCell align="right">Precio unitario</TableCell>
                        <TableCell align="right">Subtotal</TableCell>
                        <TableCell align="center" width={56} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grupo.lineas.map((linea, lineaIndex) => (
                        <TableRow key={`${linea.codigoArticulo}-${lineaIndex}`}>
                          <TableCell>{linea.codigoArticulo}</TableCell>
                          <TableCell>{linea.descripcion}</TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              value={linea.cantidad}
                              onChange={(e) => {
                                const value = Number(String(e.target.value).replace(',', '.')) || 0;
                                updateGrupo(grupoIndex, (g) => ({
                                  ...g,
                                  lineas: g.lineas.map((l, i) => (i === lineaIndex ? { ...l, cantidad: Math.max(0, value) } : l)),
                                }));
                              }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>{linea.umCompra}</TableCell>
                          <TableCell align="right">
                            <CurrencyEditField
                              value={linea.costoUnitario}
                              onChange={(value) => {
                                updateGrupo(grupoIndex, (g) => ({
                                  ...g,
                                  lineas: g.lineas.map((l, i) =>
                                    i === lineaIndex
                                      ? { ...l, costoUnitario: value, costoBrutoUnitario: value }
                                      : l,
                                  ),
                                }));
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(linea.cantidad * linea.costoUnitario)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Eliminar línea">
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Eliminar línea"
                                onClick={() => handleRemoveLinea(grupoIndex, lineaIndex)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      <UbicacionSearch
        open={openUbicacionSearch}
        onClose={() => setOpenUbicacionSearch(false)}
        onSelect={handleUbicacionSelect}
      />
    </Box>
  );
};

export default SugerenciaCompraPreview;
